import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardCheck, Lock, Unlock, Plus, Minus, FastForward, Save, DollarSign } from 'lucide-react';
import { colors, API, fmt, authHeaders } from '../utils/theme';
import { PageHeader, Select, Input, Table, Th, Td, TrHover, Badge, LoadingSpinner, EmptyState, Button } from '../components/UI';

// Fechamento de Fatura — compor a fatura ANTES de pagar.
// Conciliação responde "a compra existe?"; aqui a pergunta é "o que entra
// NESTA fatura?". Sugestões vêm do espelho TechMiles do mês; dá pra tirar,
// incluir e ANTECIPAR parcela futura com desconto. Fechar trava e cria a
// conta a pagar; o pagamento segue o caminho normal.
const MES_MIN = '2026-06';
const mesAtual = () => new Date().toISOString().slice(0, 7);

function Kpi({ label, valor, cor, sub }) {
  return (
    <div style={{ flex: '1 1 160px', minWidth: 150, padding: 14, borderRadius: 10,
      background: colors.card, border: `1px solid ${colors.border}` }}>
      <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: cor || colors.text, marginTop: 4 }}>{valor}</div>
      {sub && <div style={{ fontSize: 11, color: colors.textDim, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function FechamentoFaturaPage() {
  const [cartoes, setCartoes] = useState([]);
  const [cartaoId, setCartaoId] = useState('');
  const [mes, setMes] = useState(mesAtual());
  const [data, setData] = useState(null);
  const [itens, setItens] = useState([]); // composição em edição
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    fetch(`${API}/cartoes?limit=500`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setCartoes((d.data || d || []).sort((a, b) => (a.nome_cartao || '').localeCompare(b.nome_cartao || ''))))
      .catch(() => setCartoes([]));
  }, []);

  const carregar = useCallback(() => {
    if (!cartaoId || !mes) { setData(null); setItens([]); return; }
    setLoading(true); setErro('');
    fetch(`${API}/fechamento-fatura/${cartaoId}/${mes}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        if (d.error) { setErro(d.error); setData(null); setItens([]); return; }
        setData(d);
        setItens((d.itens || []).map(i => ({
          transacao_id: i.transacao_id, descricao: i.descricao, parcela: i.parcela,
          mes_origem: i.mes_origem, valor_original: Number(i.valor_original),
          valor_pago: Number(i.valor_pago), antecipada: !!i.antecipada,
        })));
      })
      .catch(e => setErro(e.message))
      .finally(() => setLoading(false));
  }, [cartaoId, mes]);
  useEffect(() => { carregar(); }, [carregar]);

  const fechado = data?.fechamento?.status === 'FECHADO';
  const totalOriginal = itens.reduce((s, i) => s + i.valor_original, 0);
  const totalPagar = itens.reduce((s, i) => s + i.valor_pago, 0);

  const incluir = (t, antecipada) => {
    if (fechado) return;
    let pago = Number(t.valor);
    if (antecipada) {
      // Antecipação costuma vir com desconto — pergunta o valor negociado.
      const resp = window.prompt(
        `Antecipar "${t.descricao}" (${t.mes_referencia}) — valor original ${fmt(t.valor)}.\nValor A PAGAR com desconto (vazio = sem desconto):`);
      if (resp === null) return;
      if (String(resp).trim() !== '') {
        pago = parseFloat(String(resp).replace(/\./g, '').replace(',', '.'));
        if (!(pago > 0) || pago > Number(t.valor)) { alert('Valor inválido — o desconto só reduz.'); return; }
      }
    }
    setItens(prev => [...prev, {
      transacao_id: t.id, descricao: t.descricao,
      parcela: t.total_parcelas > 1 ? `${t.numero_parcela}/${t.total_parcelas}` : null,
      mes_origem: t.mes_referencia, valor_original: Number(t.valor),
      valor_pago: pago, antecipada,
    }]);
  };
  const tirar = (txId) => { if (!fechado) setItens(prev => prev.filter(i => i.transacao_id !== txId)); };

  const salvar = async (depois) => {
    setSalvando(true); setErro('');
    try {
      const r = await fetch(`${API}/fechamento-fatura/${cartaoId}/${mes}`, {
        method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ itens: itens.map(i => ({ transacao_id: i.transacao_id, valor_pago: i.valor_pago })) }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
      if (depois) await depois(); else carregar();
    } catch (e) { setErro(e.message); }
    finally { setSalvando(false); }
  };

  const fechar = () => {
    if (!itens.length) { alert('Inclua ao menos uma parcela.'); return; }
    if (!window.confirm(`Fechar a fatura ${mes} com ${itens.length} parcela(s), total a pagar ${fmt(totalPagar)}?\nIsso trava a composição e cria a conta a pagar.`)) return;
    salvar(async () => {
      const r = await fetch(`${API}/fechamento-fatura/${cartaoId}/${mes}/fechar`, { method: 'POST', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
      carregar();
    });
  };

  const reabrir = async () => {
    if (!window.confirm('Reabrir a composição desta fatura?')) return;
    setSalvando(true); setErro('');
    try {
      const r = await fetch(`${API}/fechamento-fatura/${cartaoId}/${mes}/reabrir`, { method: 'POST', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
      carregar();
    } catch (e) { setErro(e.message); }
    finally { setSalvando(false); }
  };

  const BlocoSugestao = ({ titulo, lista, antecipada, vazio }) => (
    <div style={{ flex: '1 1 380px', minWidth: 340, background: colors.card,
      border: `1px solid ${colors.border}`, borderRadius: 10, padding: 14, maxHeight: 420, overflowY: 'auto' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: colors.textMuted, marginBottom: 10 }}>
        {titulo} <span style={{ color: colors.textDim }}>({lista.length})</span>
      </div>
      {!lista.length ? <div style={{ fontSize: 12, color: colors.textDim }}>{vazio}</div> :
        lista.map(t => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
            borderBottom: `1px solid ${colors.border}33`, fontSize: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.descricao}</div>
              <div style={{ color: colors.textDim, fontSize: 11 }}>
                {t.mes_referencia}{t.total_parcelas > 1 ? ` · parcela ${t.numero_parcela}/${t.total_parcelas}` : ''}
                {t.fidelizado ? ` · ${t.fidelizado}` : ''}
              </div>
            </div>
            <strong style={{ whiteSpace: 'nowrap' }}>{fmt(t.valor)}</strong>
            <button onClick={() => incluir(t, antecipada)} disabled={fechado}
              title={antecipada ? 'Antecipar para esta fatura' : 'Incluir na fatura'}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
                border: `1px solid ${colors.primary}55`, background: `${colors.primary}18`,
                color: colors.primary, fontSize: 11, fontWeight: 700, cursor: fechado ? 'not-allowed' : 'pointer', opacity: fechado ? 0.4 : 1 }}>
              {antecipada ? <FastForward size={12} /> : <Plus size={12} />}{antecipada ? 'Antecipar' : 'Incluir'}
            </button>
          </div>
        ))}
    </div>
  );

  return (
    <div>
      <PageHeader title="Fechamento de Fatura"
        subtitle="Compõe a fatura antes de pagar: sugestões do TechMiles, parcelas antecipadas com desconto, e só depois vira conta a pagar" />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', padding: 12,
        marginBottom: 16, background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10 }}>
        <Select value={cartaoId} onChange={e => setCartaoId(e.target.value)} style={{ width: 'auto', minWidth: 260 }}>
          <option value="">Selecione o cartão...</option>
          {cartoes.map(c => <option key={c.id} value={c.id}>{(c.nome_cartao || '').trim()}{c.numeracao_final ? ` ···${c.numeracao_final}` : ''}</option>)}
        </Select>
        <Input type="month" value={mes} min={MES_MIN} onChange={e => setMes(e.target.value)}
          style={{ width: 'auto', colorScheme: 'dark' }} />
        {fechado && (
          <Badge text={`FECHADA em ${String(data.fechamento.fechado_em || '').slice(0, 10)}`} color={colors.success} />
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {!fechado && cartaoId && (
            <>
              <Button size="sm" icon={Save} disabled={salvando} onClick={() => salvar()}>Salvar composição</Button>
              <Button size="sm" color={colors.success} icon={Lock} disabled={salvando || !itens.length} onClick={fechar}>Fechar fatura</Button>
            </>
          )}
          {fechado && (
            <Button size="sm" variant="outline" color={colors.warning} icon={Unlock} disabled={salvando} onClick={reabrir}>Reabrir</Button>
          )}
        </div>
      </div>

      {erro && (
        <div style={{ marginBottom: 14, padding: '9px 13px', borderRadius: 8, fontSize: 12,
          background: `${colors.danger}14`, border: `1px solid ${colors.danger}44`, color: colors.danger }}>{erro}</div>
      )}

      {!cartaoId ? <EmptyState message="Escolha um cartão e um mês para compor a fatura" icon={ClipboardCheck} />
        : loading ? <LoadingSpinner /> : data && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <Kpi label="Parcelas na fatura" valor={itens.length} />
            <Kpi label="Valor original" valor={fmt(totalOriginal)} sub="é o que baixa do comprometido" />
            <Kpi label="A pagar" valor={fmt(totalPagar)} cor={colors.primary} sub="é o que sai do caixa" />
            <Kpi label="Desconto por antecipação" valor={fmt(totalOriginal - totalPagar)}
              cor={totalOriginal - totalPagar > 0 ? colors.success : colors.textMuted} />
          </div>

          {/* Composição atual */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: colors.textMuted, marginBottom: 8 }}>
              Compõem esta fatura {fechado && <Lock size={12} style={{ verticalAlign: -2 }} />}
            </div>
            {!itens.length ? (
              <EmptyState message="Nada incluído ainda — use as sugestões abaixo" icon={DollarSign} />
            ) : (
              <Table>
                <thead><tr>
                  <Th>Descrição</Th><Th>Parcela</Th><Th>Mês de origem</Th>
                  <Th align="right">Original</Th><Th align="right">A pagar</Th><Th align="center">Ação</Th>
                </tr></thead>
                <tbody>
                  {itens.map(i => (
                    <TrHover key={i.transacao_id}>
                      <Td style={{ maxWidth: 340 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.descricao}</div>
                      </Td>
                      <Td>{i.parcela || 'à vista'}</Td>
                      <Td>
                        {i.mes_origem}
                        {i.antecipada && <Badge text="ANTECIPADA" color={colors.info} />}
                      </Td>
                      <Td align="right">{fmt(i.valor_original)}</Td>
                      <Td align="right" style={{ fontWeight: 700, color: i.valor_pago < i.valor_original ? colors.success : colors.text }}>
                        {fmt(i.valor_pago)}
                      </Td>
                      <Td align="center">
                        <button onClick={() => tirar(i.transacao_id)} disabled={fechado}
                          style={{ padding: '3px 8px', borderRadius: 6, border: `1px solid ${colors.danger}55`,
                            background: `${colors.danger}14`, color: colors.danger, fontSize: 11, fontWeight: 700,
                            cursor: fechado ? 'not-allowed' : 'pointer', opacity: fechado ? 0.4 : 1,
                            display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Minus size={11} /> Tirar
                        </button>
                      </Td>
                    </TrHover>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          {/* Sugestões e antecipáveis */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <BlocoSugestao titulo={`Sugestões do mês ${mes}`} lista={data.sugestoes || []} antecipada={false}
              vazio="Nenhuma parcela do espelho TechMiles neste mês fora da composição" />
            <BlocoSugestao titulo="Parcelas futuras — antecipáveis" lista={data.antecipaveis || []} antecipada={true}
              vazio="Nenhuma parcela futura deste cartão" />
          </div>
        </>
      )}
    </div>
  );
}
